import { useState, useRef, useEffect, useCallback } from "react"

function formatTime(seconds: number) {
  if (!isFinite(seconds) || seconds < 0) return "0:00"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

export default function AudioPlayer({
  src,
  label,
  sublabel,
  volume = 1,
}: {
  src: string
  label: string
  sublabel?: string
  volume?: number
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    const audio = new Audio(src)
    audio.volume = Math.max(0, Math.min(1, volume))
    audioRef.current = audio

    audio.addEventListener("loadedmetadata", () => setDuration(audio.duration))
    audio.addEventListener("timeupdate", () => {
      if (!isDragging) setCurrentTime(audio.currentTime)
    })
    audio.addEventListener("ended", () => {
      setIsPlaying(false)
      setCurrentTime(0)
    })

    return () => {
      audio.pause()
      audio.src = ""
    }
  }, [src]) // eslint-disable-line react-hooks/exhaustive-deps

  // Sync volume changes in real time
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = Math.max(0, Math.min(1, volume))
  }, [volume])

  function togglePlay() {
    const audio = audioRef.current
    if (!audio) return
    if (isPlaying) {
      audio.pause()
      setIsPlaying(false)
    } else {
      audio.play()
      setIsPlaying(true)
    }
  }

  const seekTo = useCallback(
    (clientX: number) => {
      const bar = progressRef.current
      const audio = audioRef.current
      if (!bar || !audio || !duration) return
      const rect = bar.getBoundingClientRect()
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
      const newTime = ratio * duration
      audio.currentTime = newTime
      setCurrentTime(newTime)
    },
    [duration],
  )

  function handlePointerDown(e: React.PointerEvent) {
    setIsDragging(true)
    seekTo(e.clientX)
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
  }

  function handlePointerMove(e: React.PointerEvent) {
    if (!isDragging) return
    seekTo(e.clientX)
  }

  function handlePointerUp() {
    setIsDragging(false)
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className="flex items-center gap-3">
      {/* Play/pause button */}
      <button
        type="button"
        onClick={togglePlay}
        className={`flex size-10 shrink-0 items-center justify-center rounded-full transition-colors ${
          isPlaying
            ? "bg-red text-white"
            : "bg-charcoal text-white hover:bg-charcoal/80"
        }`}
      >
        {isPlaying ? (
          <svg
            className="size-5"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.75 5.25v13.5m-7.5-13.5v13.5"
            />
          </svg>
        ) : (
          <svg className="size-5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5.14v14l11-7-11-7z" />
          </svg>
        )}
      </button>

      {/* Track info + progress */}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <p className="truncate text-sm font-medium text-charcoal">{label}</p>
          <p className="shrink-0 text-[11px] tabular-nums text-warm-grey">
            {formatTime(currentTime)} / {formatTime(duration)}
          </p>
        </div>

        {/* Seekable progress bar */}
        <div
          ref={progressRef}
          className="group relative h-2 cursor-pointer rounded-full bg-stone/30"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full bg-charcoal transition-[width] duration-75"
            style={{ width: `${progress}%` }}
          />
          {/* Thumb */}
          <div
            className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-charcoal bg-white opacity-0 shadow-sm transition-opacity group-hover:opacity-100"
            style={{ left: `${progress}%`, opacity: isDragging ? 1 : undefined }}
          />
        </div>

        {sublabel && (
          <p className="mt-1 text-[11px] text-warm-grey">{sublabel}</p>
        )}
      </div>
    </div>
  )
}
